"use client";
import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../../supabaseClient";
import { recordApplicationAudit } from "../../../utils/audit";
import { formatProfileName } from "../../../utils/profileName";
import Header from "../../../components/IntranetHeader";
import ClinicalPatientWorkspace from "../../../components/ClinicalPatientWorkspace";
import AdvancedClinicalWorkspace from "../../../components/AdvancedClinicalWorkspace";
import { FaTimes } from "react-icons/fa";

// --------------------
// Patient alert options
// --------------------
const PATIENT_ALERT_OPTIONS = [
  "Medically Unstable",
  "Aggressive",
  "High Falls Risk",
  "Infection Risk",
  "Safeguarding Concern",
  "Requires Close Observation / Restraints",
];

// Alerts that immediately make the patient HIGH RISK
const HIGH_RISK_ALERTS = [
  "Medically Unstable",
  "Aggressive",
  "Requires Close Observation / Restraints",
];

// Entry templates
const entryTemplates = [
  {
    label: "Initial Assessment",
    value: `Initial Assessment

Presenting Complaint:
History of Presenting Complaint:
Relevant Background:
Examination / Findings:
Working Impression:
Plan:
Escalation / Senior Review:`
  },
  {
    label: "Progress Note",
    value: `Progress Note

Current Status:
Changes Since Last Review:
Assessment:
Interventions:
Outstanding Tasks:
Plan:`
  },
  {
    label: "Nursing Note",
    value: `Nursing Note

Patient Status:
Observations / Concerns:
Mobility / Personal Care:
Nutrition / Hydration:
Medication / Treatment:
Communication / Family:
Plan / Handover:`
  },
  {
    label: "Consultant Review",
    value: `Consultant Review

Clinical Summary:
Review Findings:
Results Reviewed:
Impression:
Decision / Plan:
Follow-up / Escalation:`
  },
  {
    label: "Procedure Note",
    value: `Procedure Note

Procedure:
Indication:
Consent Confirmed:
Staff Present:
Procedure Details:
Outcome:
Complications:
Post-procedure Plan:`
  },
  {
    label: "Safeguarding Note",
    value: `Safeguarding Note

Concern Identified:
Information Shared:
Immediate Safety Actions:
People / Teams Informed:
Plan:
Follow-up Required:`
  },
  {
    label: "Triage",
    value: `Triage Template

Presenting Complaint:
Past Medical History:
Known Allergies:
Temperature:
Heart Rate:
Blood Pressure:
Respiratory Rate:
Oxygen Saturation (SpO2):
Pain Score (1-10):
Priority Level:
Next Steps:`,
  },
  {
    label: "Pathology Test Requests",
    value: `Pathology Test Requests

Your Name:
Date:
Time:
Tests Requested:
Reasons:
Priority (Y/N):
Is Patient Fasting (Y/N):`,
  },
  {
    label: "Test Request",
    value: `Test Request

Your Name:
Date:
Time:
Patient's Current Location (Bed/Ward):
Type of Imaging/Test:`,
  },
  {
    label: "Medication Administered",
    value: `Medication Administered

Your Name:
Time Administered:
Medication Administered:
Route:`,
  },
  {
    label: "Patient Prescription",
    value: `Patient Prescription

Patient's Name:
Your Name:
Your Role:
Date:
Time:
Medication Name:
Drug Strength (NOT Dose):
Drug Form:
Dose:
Frequency:
Route:
Course Length (If Needed):
Notes:`,
  },
  {
    label: "Handover/Transfer Notes",
    value: `Handover/Transfer Notes

Your Name:
Your Role:
Patient's Name:
Patient's Diagnosis:
Current Ward:
Moving To:
Any Current Prescribed Medication:
Most Recent Set of Observations:
Notes:`,
  },
  {
    label: "Cardiac Arrest",
    value: `Witnessed or Unwitnessed:
Time CPR Commenced:

-Staff Present-
Lead:
Airway:
CPR:
Defib:
Drugs:
Scribe:

Airway:
Reversible Causes Considered:

-RHYTHM CHECK-
CPR Cycle:
Rhythm (Asystole/PEA/VF/pVT):
Drugs:
Shock Given:
ROSC (Y/N + Time):

Reason CPR Stopped (If Applicable):`,
  },
];

const formatForDatetimeLocal = (dt) => {
  if (!dt) return "";
  const d = new Date(dt);
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate(),
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDisplayDate = (dt) => {
  if (!dt) return "";
  const d = new Date(dt);
  const pad = (n) => n.toString().padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const formatRoleplayNhsNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length !== 10) return digits || "Not assigned";
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

export default function PatientRecord() {
  const { id: patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [entries, setEntries] = useState([]);
  const [careTeam, setCareTeam] = useState([]);
  const [newEntry, setNewEntry] = useState("");
  const [newCareMember, setNewCareMember] = useState({
    staff_name: "",
    role: "",
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [profileCache, setProfileCache] = useState({});
  const [portalProfile, setPortalProfile] = useState(null);
  const [portalProfileImageUrl, setPortalProfileImageUrl] = useState("");

  useEffect(() => {
    const fetchUser = async () => {
      const { data } = await supabase.auth.getUser();
      setCurrentUser(data.user || null);
    };
    fetchUser();
  }, []);

  const lookupUserName = async (userId) => {
    if (!userId) return "Unknown";
    if (profileCache[userId]) return profileCache[userId];

    const { data: profileData } = await supabase
      .from("profiles")
      .select("full_name, name_prefix")
      .eq("id", userId)
      .single();

    const name = formatProfileName(profileData, "Unknown");
    setProfileCache((prev) => ({ ...prev, [userId]: name }));
    return name;
  };

  useEffect(() => {
    if (!patientId) return;

    const fetchPortalProfile = async (patientData) => {
      let query = supabase
        .from("patient_portal_accounts")
        .select(`
          id,
          roleplay_nhs_number,
          roleplay_name,
          roblox_username,
          pronouns,
          height_cm,
          weight_kg,
          allergies,
          preferences,
          accessibility_needs,
          communication_preferences,
          profile_image_path
        `);

      if (patientData.portal_account_id) {
        query = query.eq("id", patientData.portal_account_id);
      } else if (patientData.roblox_username) {
        query = query.ilike("roblox_username", patientData.roblox_username);
      } else {
        setPortalProfile(null);
        setPortalProfileImageUrl("");
        return;
      }

      const { data: profileData, error: profileError } = await query
        .limit(1)
        .maybeSingle();

      if (profileError) {
        console.error("Unable to load patient portal details:", profileError);
        setPortalProfile(null);
        setPortalProfileImageUrl("");
        return;
      }

      setPortalProfile(profileData || null);

      if (profileData?.profile_image_path) {
        const { data: signedImage } = await supabase.storage
          .from("patient-profile-images")
          .createSignedUrl(profileData.profile_image_path, 60 * 60);
        setPortalProfileImageUrl(signedImage?.signedUrl || "");
      } else if (profileData?.roblox_username) {
        try {
          const response = await fetch("/api/robloxavatar", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username: profileData.roblox_username }),
          });
          const result = await response.json().catch(() => ({}));
          setPortalProfileImageUrl(result?.imageUrl || "");
        } catch {
          setPortalProfileImageUrl("");
        }
      } else {
        setPortalProfileImageUrl("");
      }
    };

    const fetchPatient = async () => {
      const { data, error } = await supabase
        .from("patients")
        .select("*")
        .eq("id", patientId)
        .single();

      if (error) console.error(error);
      else {
        setPatient({
          ...data,
          alerts: data.alerts || [],
        });

        await fetchPortalProfile(data);

        void recordApplicationAudit({
          action: "VIEW",
          tableName: "patients",
          recordId: patientId,
          summary: "Patient record viewed",
          metadata: { route: window.location.pathname },
        });
      }
    };

    const fetchEntries = async () => {
      const { data, error } = await supabase
        .from("patient_records")
        .select("*")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: true });

      if (error) console.error(error);
      else {
        const withNames = await Promise.all(
          data.map(async (entry) => ({
            ...entry,
            added_by: await lookupUserName(entry.added_by),
          })),
        );
        setEntries(withNames);
      }
    };

    const fetchCareTeam = async () => {
      const { data, error } = await supabase
        .from("patient_care_team")
        .select("*")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: true });

      if (error) console.error(error);
      else {
        const withNames = await Promise.all(
          data.map(async (member) => ({
            ...member,
            added_by: await lookupUserName(member.added_by),
          })),
        );
        setCareTeam(withNames);
      }
    };

    fetchPatient();
    fetchEntries();
    fetchCareTeam();
  }, [patientId]);

  const handlePatientChange = (field, value) =>
    setPatient((prev) => ({ ...prev, [field]: value }));

  // --------------------
  // Alerts toggle
  // --------------------
  const toggleAlert = (alert) => {
    setPatient((prev) => {
      const current = prev.alerts || [];
      if (current.includes(alert)) {
        return {
          ...prev,
          alerts: current.filter((a) => a !== alert),
        };
      }
      return {
        ...prev,
        alerts: [...current, alert],
      };
    });
  };

  const handleDownloadCharts = () => {
    if (!patient) return;

    const printWindow = window.open("", "_blank");

    const entriesHtml = entries
      .map(
        (entry) => `
        <div class="entry">
          <strong>${formatDisplayDate(entry.created_at)} – ${entry.added_by}</strong>
          <pre>${entry.entry}</pre>
        </div>
      `,
      )
      .join("");

    const alertsHtml =
      patient.alerts && patient.alerts.length > 0
        ? `
        <ul>
          ${patient.alerts.map((alert) => `<li>${alert}</li>`).join("")}
        </ul>
      `
        : `<p>No active alerts</p>`;

    printWindow.document.write(`
    <html>
      <head>
        <title>Clinical Record</title>
        <style>

          @page {
            margin: 20mm;
          }

          body {
            font-family: Arial, sans-serif;
            margin: 0;
          }

          .page-header {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            height: 100px;
            display: flex;
            align-items: center;
            gap: 20px;
            border-bottom: 3px solid #005eb8;
            padding: 20px 40px;
            background: white;
          }

          .logo {
            height: 60px;
          }

          .content {
            padding: 140px 40px 40px 40px;
          }

          h1 {
            margin: 0;
          }

          h2 {
            margin-top: 30px;
            border-bottom: 1px solid #ccc;
            padding-bottom: 4px;
          }

          p {
            margin: 4px 0;
          }

          ul {
            margin: 8px 0;
            padding-left: 20px;
          }

          .entry {
            margin-bottom: 16px;
            page-break-inside: avoid;
          }

          pre {
            white-space: pre-wrap;
            margin: 4px 0 0 0;
            font-family: inherit;
          }

          .footer {
            margin-top: 40px;
            font-size: 12px;
          }

        </style>
      </head>

      <body>

        <div class="page-header">
          <img src="https://ruht.cloud/logo.png" class="logo" />
          <h1>Clinical Record</h1>
        </div>

        <div class="content">

          <h2>Patient Details</h2>
          <p><strong>Name:</strong> ${patient.full_name || ""}</p>
          <p><strong>Roleplay NHS number:</strong> ${formatRoleplayNhsNumber(patient.roleplay_nhs_number || portalProfile?.roleplay_nhs_number)}</p>
          <p><strong>Sex:</strong> ${patient.sex || ""}</p>
          <p><strong>Age:</strong> ${patient.age || ""}</p>
          <p><strong>Presenting Complaint:</strong> ${patient.presenting_complaint || ""}</p>
          <p><strong>Arrival:</strong> ${formatDisplayDate(patient.arrival_time)}</p>
          <p><strong>Location:</strong> ${patient.location || ""}</p>
          <p><strong>Triage Band:</strong> ${patient.triage_band || ""}</p>
          <p><strong>Bed:</strong> ${patient.bed || ""}</p>

          <h2>Patient Alert / Flagging</h2>
          ${alertsHtml}

          <h2>Clinical Entries</h2>
          ${entriesHtml || "<p>No entries recorded</p>"}

          <div class="footer">
            Confidential – For Clinical Use Only
          </div>

        </div>

        <script>
          window.onload = function() {
            window.print();
          };
        </script>

      </body>
    </html>
  `);

    printWindow.document.close();
  };
  // --------------------
  // Alert severity logic
  // --------------------
  const alertStatus = useMemo(() => {
    const alerts = patient?.alerts || [];

    const hasHighRisk = alerts.some((a) => HIGH_RISK_ALERTS.includes(a));

    if (hasHighRisk || alerts.length >= 3) {
      return {
        level: "red",
        title: "High risk",
      };
    }

    if (alerts.length >= 2) {
      return {
        level: "yellow",
        title: "Moderate risk",
      };
    }

    if (alerts.length === 1) {
      return {
        level: "green",
        title: "Low risk",
      };
    }

    return {
      level: "none",
      title: "No active patient alerts",
    };
  }, [patient]);

  const handlePatientSave = async () => {
    const { error } = await supabase
      .from("patients")
      .update({
        full_name: patient.full_name,
        sex: patient.sex,
        age: patient.age,
        presenting_complaint: patient.presenting_complaint,
        arrival_time: patient.arrival_time,
        location: patient.location,
        triage_band: patient.triage_band,
        bed: patient.bed,
        alerts: patient.alerts || [],
      })
      .eq("id", patientId);

    if (error) console.error(error);
    else alert("Patient updated successfully!");
  };

  const handleArchivePatient = async () => {
    const newValue = !patient.archived;

    const { error } = await supabase
      .from("patients")
      .update({ archived: newValue, archived_at: newValue ? new Date().toISOString() : null })
      .eq("id", patientId);

    if (error) {
      console.error(error);
      alert("Error archiving patient");
    } else {
      setPatient((p) => ({ ...p, archived: newValue }));
      alert(newValue ? "Patient archived" : "Patient unarchived");
    }
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (!newEntry || !currentUser) return;

    const { data, error } = await supabase
      .from("patient_records")
      .insert([
        {
          patient_id: patientId,
          entry: newEntry,
          added_by: currentUser.id,
        },
      ])
      .select();

    if (error) {
      console.error(error);
    } else {
      const name = await lookupUserName(data[0].added_by);
      setEntries((prev) => [...prev, { ...data[0], added_by: name }]);
      setNewEntry("");
    }
  };

  const handleAddCareMember = async (e) => {
    e.preventDefault();
    if (!newCareMember.staff_name || !newCareMember.role || !currentUser)
      return;

    const { data, error } = await supabase
      .from("patient_care_team")
      .insert([
        {
          patient_id: patientId,
          staff_name: newCareMember.staff_name,
          role: newCareMember.role,
          added_by: currentUser.id,
        },
      ])
      .select();

    if (error) {
      console.error(error);
    } else {
      const name = await lookupUserName(data[0].added_by);
      setCareTeam((prev) => [...prev, { ...data[0], added_by: name }]);
      setNewCareMember({ staff_name: "", role: "" });
    }
  };

  if (!patient) return <div>Loading patient…</div>;

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <div className="w-full px-6 mt-5 flex-1">
        <div className="flex justify-between items-center mb-2">
          <h1 className="nhsuk-heading-l">
            Patient Record: {patient.full_name}
            {patient.archived ? " - Archived" : ""}
          </h1>
          <Link
            to="/intranet/ecamis/index"
            className="text-black hover:text-gray-700"
            aria-label="Close patient record"
          >
            <FaTimes size={24} />
          </Link>
        </div>

        {/* ------------------------------------------------
            Patient alert status box (NHS frontend styles)
        ------------------------------------------------ */}
        {alertStatus.level === "red" && (
          <div
            className="nhsuk-error-summary"
            aria-labelledby="alert-status-title"
            role="alert"
            tabIndex="-1"
          >
            <h2 className="nhsuk-error-summary__title" id="alert-status-title">
              {alertStatus.title}
            </h2>
            <div className="nhsuk-error-summary__body">
              <p>
                Active alerts:{" "}
                {(patient.alerts || []).length
                  ? patient.alerts.join(", ")
                  : "None"}
              </p>
            </div>
          </div>
        )}

        {alertStatus.level === "yellow" && (
          <div className="nhsuk-warning-callout">
            <h3 className="nhsuk-warning-callout__label">
              {alertStatus.title}
            </h3>
            <p>
              Active alerts:{" "}
              {(patient.alerts || []).length
                ? patient.alerts.join(", ")
                : "None"}
            </p>
          </div>
        )}

        {alertStatus.level === "green" && (
          <div className="nhsuk-inset-text">
            <span className="nhsuk-u-visually-hidden">Information: </span>
            <strong>{alertStatus.title}</strong>
            <br />
            Active alert: {(patient.alerts || []).join(", ")}
          </div>
        )}

        {alertStatus.level === "none" && (
          <div className="nhsuk-inset-text">
            <span className="nhsuk-u-visually-hidden">Information: </span>
            {alertStatus.title}
          </div>
        )}

        <div className="layout grid grid-cols-[380px_1fr] gap-6 mt-4">
          {/* Sidebar */}
          <aside className="sidebar border border-gray-300 p-4 rounded space-y-4">
            <label>Full Name</label>
            <input
              className="nhsuk-input"
              value={patient.full_name || ""}
              onChange={(e) => handlePatientChange("full_name", e.target.value)}
            />

            <label>Roblox Username</label>
            <input
              className="nhsuk-input"
              value={patient.roblox_username || ""}
              onChange={(e) =>
                handlePatientChange("roblox_username", e.target.value)
              }
            />

            <label>Roleplay NHS Number</label>
            <input
              className="nhsuk-input"
              value={formatRoleplayNhsNumber(
                patient.roleplay_nhs_number || portalProfile?.roleplay_nhs_number,
              )}
              readOnly
            />
            <div className="nhsuk-hint">RUHT roleplay identifier only.</div>

            <label>Sex</label>
            <input
              className="nhsuk-input"
              value={patient.sex || ""}
              onChange={(e) => handlePatientChange("sex", e.target.value)}
            />

            <label>Age</label>
            <input
              className="nhsuk-input"
              value={patient.age || ""}
              onChange={(e) => handlePatientChange("age", e.target.value)}
            />

            <label>Presenting Complaint</label>
            <textarea
              className="nhsuk-textarea"
              value={patient.presenting_complaint || ""}
              onChange={(e) =>
                handlePatientChange("presenting_complaint", e.target.value)
              }
            />

            <label>Arrival Time</label>
            <input
              type="datetime-local"
              className="nhsuk-input"
              value={formatForDatetimeLocal(patient.arrival_time)}
              onChange={(e) =>
                handlePatientChange("arrival_time", e.target.value)
              }
            />

            <label>Location</label>
            <input
              className="nhsuk-input"
              value={patient.location || ""}
              onChange={(e) => handlePatientChange("location", e.target.value)}
            />

            <label>Triage Band</label>
            <input
              className="nhsuk-input"
              value={patient.triage_band || ""}
              onChange={(e) =>
                handlePatientChange("triage_band", e.target.value)
              }
            />

            <label>Bed</label>
            <input
              className="nhsuk-input"
              value={patient.bed || ""}
              onChange={(e) => handlePatientChange("bed", e.target.value)}
            />

            {/* Patient Alert / Flagging */}
            <div className="border-t pt-3">
              <h3 className="nhsuk-heading-s mb-2">Patient Alert / Flagging</h3>

              <div className="nhsuk-form-group">
                <div className="nhsuk-checkboxes">
                  {PATIENT_ALERT_OPTIONS.map((alert, index) => {
                    const id = `patient-alert-${index}`;

                    return (
                      <div className="nhsuk-checkboxes__item" key={alert}>
                        <input
                          className="nhsuk-checkboxes__input"
                          id={id}
                          name="patient-alerts"
                          type="checkbox"
                          checked={(patient.alerts || []).includes(alert)}
                          onChange={() => toggleAlert(alert)}
                        />
                        <label
                          className="nhsuk-label nhsuk-checkboxes__label"
                          htmlFor={id}
                        >
                          {alert}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <button
              className="nhsuk-button w-full mt-2"
              onClick={handlePatientSave}
            >
              Save Patient Details
            </button>

            <button
              className="nhsuk-button w-full mt-2"
              onClick={handleArchivePatient}
            >
              {patient.archived ? "Unarchive Patient" : "Archive Patient"}
            </button>

            <button
              className="nhsuk-button w-full mt-2"
              onClick={handleDownloadCharts}
            >
              Download Charts
            </button>
          </aside>

          {/* Main content */}
          <main>
            {portalProfile && (
              <section className="nhsuk-section mb-4 border border-gray-300 p-4 rounded">
                <h2 className="nhsuk-heading-m">Patient-provided profile</h2>
                <p className="nhsuk-hint">
                  Roleplay details supplied through the patient portal.
                </p>

                <div className="pguh-portal-profile-summary">
                  {portalProfileImageUrl && (
                    <div className="pguh-portal-profile-summary__image">
                      <img
                        src={portalProfileImageUrl}
                        alt={`${portalProfile.roleplay_name || patient.full_name} profile`}
                      />
                    </div>
                  )}

                  <dl className="pguh-portal-profile-summary__details">
                    <div>
                      <dt>Roleplay NHS number</dt>
                      <dd>
                        <strong>
                          {formatRoleplayNhsNumber(
                            portalProfile.roleplay_nhs_number ||
                              patient.roleplay_nhs_number,
                          )}
                        </strong>
                      </dd>
                    </div>
                    <div>
                      <dt>Roleplay name</dt>
                      <dd>{portalProfile.roleplay_name || "Not provided"}</dd>
                    </div>
                    <div>
                      <dt>Pronouns</dt>
                      <dd>{portalProfile.pronouns || "Not provided"}</dd>
                    </div>
                    <div>
                      <dt>Height</dt>
                      <dd>
                        {portalProfile.height_cm != null
                          ? `${portalProfile.height_cm} cm`
                          : "Not provided"}
                      </dd>
                    </div>
                    <div>
                      <dt>Weight</dt>
                      <dd>
                        {portalProfile.weight_kg != null
                          ? `${portalProfile.weight_kg} kg`
                          : "Not provided"}
                      </dd>
                    </div>
                    <div>
                      <dt>Allergies</dt>
                      <dd>{portalProfile.allergies || "Not provided"}</dd>
                    </div>
                    <div>
                      <dt>Accessibility needs</dt>
                      <dd>
                        {portalProfile.accessibility_needs || "Not provided"}
                      </dd>
                    </div>
                    <div>
                      <dt>Communication preferences</dt>
                      <dd>
                        {portalProfile.communication_preferences ||
                          "Not provided"}
                      </dd>
                    </div>
                    <div>
                      <dt>Other preferences</dt>
                      <dd>{portalProfile.preferences || "Not provided"}</dd>
                    </div>
                  </dl>
                </div>
              </section>
            )}

            <ClinicalPatientWorkspace
              patient={patient}
              portalProfile={portalProfile}
              currentUser={currentUser}
              onPatientDischarged={() =>
                setPatient((current) => ({ ...current, archived: true, bed: null }))
              }
            />

            <AdvancedClinicalWorkspace
              patient={patient}
              portalProfile={portalProfile}
              currentUser={currentUser}
            />

            {/* Care Team */}
            <section className="nhsuk-section mb-4 border border-gray-300 p-4 rounded">
              <h2 className="nhsuk-heading-m">Care Team</h2>

              <form
                onSubmit={handleAddCareMember}
                className="mb-4 grid grid-cols-2 gap-2"
              >
                <input
                  className="nhsuk-input"
                  placeholder="Staff Name"
                  value={newCareMember.staff_name}
                  onChange={(e) =>
                    setNewCareMember((p) => ({
                      ...p,
                      staff_name: e.target.value,
                    }))
                  }
                />
                <input
                  className="nhsuk-input"
                  placeholder="Role"
                  value={newCareMember.role}
                  onChange={(e) =>
                    setNewCareMember((p) => ({ ...p, role: e.target.value }))
                  }
                />
                <button className="nhsuk-button col-span-2">Add Staff</button>
              </form>

              <ul className="nhsuk-list">
                {careTeam.map((member) => (
                  <li key={member.id} className="mb-2">
                    <strong>{member.staff_name}</strong>
                    <br />
                    {member.role} • added by {member.added_by} •{" "}
                    {formatDisplayDate(member.created_at)}
                  </li>
                ))}
              </ul>
            </section>

            {/* Entries */}
            <section className="nhsuk-section mb-4 border border-gray-300 p-4 rounded">
              <h2 className="nhsuk-heading-m">Entries</h2>

              <form onSubmit={handleAddEntry} className="mb-4">
                <select
                  className="nhsuk-select mb-2 w-full"
                  value={newEntry}
                  onChange={(e) => setNewEntry(e.target.value)}
                >
                  <option value="">-- Entry Templates --</option>
                  {entryTemplates.map((opt, idx) => (
                    <option key={idx} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>

                <textarea
                  className="nhsuk-textarea mb-2 w-full"
                  value={newEntry}
                  onChange={(e) => setNewEntry(e.target.value)}
                  rows={6}
                />

                <button className="nhsuk-button">Add Entry</button>
              </form>

              <ul className="nhsuk-list">
                {entries.map((entry) => (
                  <li key={entry.id} className="mb-3">
                    <strong>
                      {formatDisplayDate(entry.created_at)} – {entry.added_by}
                    </strong>
                    <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                      {entry.entry}
                    </pre>
                  </li>
                ))}
              </ul>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
